import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";

const UpdateQwikMedicUserModal = ({ isOpen, toggle, user, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    phonenumber: "",
    email: "",
    city: "",
    gender: "",
    bloodgroup: "",
    occupation: "",
    area: "",
    insidedhaka: true,
    blockstatus: false,
    blockreason: "",
    qmoney: "0",
    qcoins: "0",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        phonenumber: user.phonenumber || "",
        email: user.email || "",
        city: user.city || "",
        gender: user.gender || "",
        bloodgroup: user.bloodgroup || "",
        occupation: user.occupation || "",
        area: user.area || "",
        insidedhaka: user.insidedhaka !== undefined ? user.insidedhaka : true,
        blockstatus: user.blockstatus || false,
        blockreason: user.blockreason || "",
        qmoney: user.qmoney || "0",
        qcoins: user.qcoins || "0",
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.phonenumber.trim()) {
      newErrors.phonenumber = "Phone number is required";
    } else if (!/^\d{11}$/.test(formData.phonenumber)) {
      newErrors.phonenumber = "Phone number must be 11 digits";
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (formData.qmoney && isNaN(formData.qmoney)) {
      newErrors.qmoney = "QMoney must be a number";
    }

    if (formData.qcoins && isNaN(formData.qcoins)) {
      newErrors.qcoins = "QCoins must be a number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error("Failed to update user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData({
      name: "",
      phonenumber: "",
      email: "",
      city: "",
      gender: "",
      bloodgroup: "",
      occupation: "",
      area: "",
      insidedhaka: true,
      blockstatus: false,
      blockreason: "",
      qmoney: "0",
      qcoins: "0",
    });
    setErrors({});
    toggle();
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose} size="lg">
      <ModalHeader toggle={handleModalClose}>
        <i className="fas fa-user-edit me-2"></i>
        Edit User: {user.name}
      </ModalHeader>
      <ModalBody>
        <div className="row">
          {/* Basic Information */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Name *</label>
            <input
              type="text"
              name="name"
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              value={formData.name}
              onChange={handleChange}
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Phone Number *</label>
            <input
              type="text"
              name="phonenumber"
              className={`form-control ${errors.phonenumber ? 'is-invalid' : ''}`}
              value={formData.phonenumber}
              onChange={handleChange}
              readOnly
            />
            {errors.phonenumber && <div className="invalid-feedback">{errors.phonenumber}</div>}
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">City *</label>
            <input
              type="text"
              name="city"
              className={`form-control ${errors.city ? 'is-invalid' : ''}`}
              value={formData.city}
              onChange={handleChange}
            />
            {errors.city && <div className="invalid-feedback">{errors.city}</div>}
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Gender</label>
            <select
              name="gender"
              className="form-control"
              value={formData.gender}
              onChange={handleChange}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Blood Group</label>
            <select
              name="bloodgroup"
              className="form-control"
              value={formData.bloodgroup}
              onChange={handleChange}
            >
              <option value="">Select Blood Group</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Occupation</label>
            <input
              type="text"
              name="occupation"
              className="form-control"
              value={formData.occupation}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Area</label>
            <input
              type="text"
              name="area"
              className="form-control"
              value={formData.area}
              onChange={handleChange}
            />
          </div>

          {/* Wallet Information */}
          {/* <div className="col-md-6 mb-3">
            <label className="form-label">QMoney</label>
            <input
              type="number"
              name="qmoney"
              className={`form-control ${errors.qmoney ? 'is-invalid' : ''}`}
              value={formData.qmoney}
              onChange={handleChange}
              step="0.01"
            />
            {errors.qmoney && <div className="invalid-feedback">{errors.qmoney}</div>}
          </div> */}

          {/* <div className="col-md-6 mb-3">
            <label className="form-label">QCoins</label>
            <input
              type="number"
              name="qcoins"
              className={`form-control ${errors.qcoins ? 'is-invalid' : ''}`}
              value={formData.qcoins}
              onChange={handleChange}
            />
            {errors.qcoins && <div className="invalid-feedback">{errors.qcoins}</div>}
          </div> */}

          {/* Location */}
          <div className="col-md-6 mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                id="insidedhaka"
                name="insidedhaka"
                className="form-check-input"
                checked={formData.insidedhaka}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="insidedhaka">
                Inside Dhaka
              </label>
            </div>
          </div>

          {/* Account Status */}
          <div className="col-md-6 mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                id="blockstatus"
                name="blockstatus"
                className="form-check-input"
                checked={formData.blockstatus}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="blockstatus">
                Block User
              </label>
            </div>
          </div>

          {formData.blockstatus && (
            <div className="col-md-12 mb-3">
              <label className="form-label">Block Reason</label>
              <textarea
                name="blockreason"
                className="form-control"
                value={formData.blockreason}
                onChange={handleChange}
                rows="2"
                placeholder="Reason for blocking user"
              />
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleModalClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Updating...
            </>
          ) : (
            <>
              <i className="fas fa-save me-2"></i>
              Update User
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UpdateQwikMedicUserModal;